import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  Room, 
  RoomEvent,
  Track,
  setLogLevel,
  LogLevel,
} from 'livekit-client';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  Button,
  Container,
  Drawer,
  Divider,
  Avatar,
  Card,
  CardContent,
  useMediaQuery,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PeopleIcon from '@mui/icons-material/People';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotesIcon from '@mui/icons-material/Notes';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import Cancel from '@mui/icons-material/Cancel';
import { useAuthStore } from '../../stores/authStore';
import { useCallStore } from '../../stores/callStore';
import { apiRequest } from '../../utils/api';
import AgentVisualizer from './AgentVisualizer';
import './VideoCall.css';

function VideoCall() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isObserverMode = searchParams.get('mode') === 'observer';
  const { user } = useAuthStore();
  const { setCurrentCall } = useCallStore();
  
  // Estados del componente
  const [room, setRoom] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [, setLocalTracks] = useState({ audio: null, video: null });
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [callInfo, setCallInfo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [notes, setNotes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isMobile = useMediaQuery('(max-width:768px)');

  // Referencias para elementos de video y control de conexión
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const remoteAudioRefs = useRef({});
  const roomRef = useRef(null);
  const isConnectingRef = useRef(false);
  const hasConnectedRef = useRef(false);
  const connectAttemptRef = useRef(0);
  const mountTimeRef = useRef(Date.now());

  // Obtener información de la llamada
  const fetchCallInfo = useCallback(async () => {
    try {
      const response = await apiRequest(`/calls/${callId}`);
      setCallInfo(response.call);
      setIsRecording(response.call.isRecording);
      setCurrentCall(response.call);
    } catch (error) {
      console.error('Error fetching call info:', error);
      setError('No se pudo obtener la información de la llamada');
    }
  }, [callId, setCurrentCall]);

  // Obtener notas existentes de la llamada desde la base de datos
  const fetchCallNotes = useCallback(async () => {
    try {
      const response = await apiRequest(`/calls/${callId}/notes`);

      if (response.notes && response.notes.length > 0) {
        // Convertir las notas de la BD al formato esperado por el componente
        const formattedNotes = [];
        const formattedSuggestions = [];

        response.notes.forEach((note) => {
          const formattedNote = {
            id: note.id,
            text: note.content,
            timestamp: new Date(note.created_at),
            type: note.note_type,
            source: 'database', // Marcar que viene de la BD
          };

          // Clasificar según el tipo de nota
          if (note.note_type === 'ai_suggestion') {
            formattedSuggestions.push(formattedNote);
          } else {
            // ai_summary, ai_transcript, o cualquier otro tipo va a notas
            formattedNotes.push(formattedNote);
          }
        });

        // Establecer las notas y sugerencias existentes
        if (formattedNotes.length > 0) {
          setNotes(formattedNotes);
          console.log(`Cargadas ${formattedNotes.length} notas desde la base de datos`);
        }
        if (formattedSuggestions.length > 0) {
          setSuggestions(formattedSuggestions);
          console.log(`Cargadas ${formattedSuggestions.length} sugerencias desde la base de datos`);
        }
      }
    } catch (error) {
      console.error('Error fetching call notes:', error);
      // No mostrar error al usuario, las notas son opcionales
    }
  }, [callId]);

  // Obtener token de Livekit
  const getLivekitToken = async () => {
    // If navigated from a direct call (ai_direct or family_direct), use the pre-generated token
    if (location.state?.token && location.state?.serverUrl) {
      return { token: location.state.token, serverUrl: location.state.serverUrl };
    }

    try {
      // Observer mode: get livestream token (hidden, subscribe-only)
      if (isObserverMode) {
        const response = await apiRequest(`/calls/${callId}/livestream-token`);
        console.log('[VideoCall] Livestream token obtenido (observer mode)');
        return { token: response.token, serverUrl: response.serverUrl || response.server_url };
      }

      const response = await apiRequest('/calls/token', {
        method: 'POST',
        body: JSON.stringify({
          call_id: callId,
          participant_id: user.id,
          role: user.role,
          is_observer: false
        })
      });

      console.log('[VideoCall] Token de LiveKit obtenido exitosamente');
      return { token: response.token, serverUrl: response.server_url };
    } catch (error) {
      console.error('[VideoCall] Error al obtener token de LiveKit:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      // Si es un error 401, podría ser que el token de autenticación expiró
      if (error.response?.status === 401) {
        throw new Error('Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No se pudo obtener el token de acceso para la videollamada');
    }
  };

  // Conectar a la sala de Livekit
  const connectToRoom = async () => {
    // Evitar múltiples intentos simultáneos o reconexiones innecesarias
    if (isConnectingRef.current || hasConnectedRef.current || roomRef.current) {
      console.log('Already connecting/connected, skipping connectToRoom');
      return;
    }
    isConnectingRef.current = true;
    const attemptId = Date.now();
    connectAttemptRef.current = attemptId;
    try {
      setIsLoading(true);
      setError(null);

      // Obtener token
      const { token, serverUrl } = await getLivekitToken();
      
      // Crear y configurar sala
      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          videoEncoding: {
            maxBitrate: 1_500_000,
            maxFramerate: 30,
          },
        },
      });

      // Configurar event listeners
      newRoom.on(RoomEvent.Connected, () => {
        if (connectAttemptRef.current !== attemptId) {
          return;
        }
        console.log('Connected to room');
        setIsConnected(true);
        setIsLoading(false);
        hasConnectedRef.current = true;
        isConnectingRef.current = false;
        updateParticipants(newRoom);
      });

      newRoom.on(RoomEvent.Reconnecting, () => {
        console.log('Reconnecting to room...');
        setIsConnected(false);
        // No marcar error aquí; es transitorio
      });

      newRoom.on(RoomEvent.Reconnected, () => {
        console.log('Reconnected to room');
        setIsConnected(true);
        setError(null);
      });

      newRoom.on(RoomEvent.Disconnected, (reason) => {
        if (connectAttemptRef.current !== attemptId) {
          return;
        }
        console.log('Disconnected from room:', reason);
        setIsConnected(false);
        hasConnectedRef.current = false;
        isConnectingRef.current = false;
        // No forzar error en códigos numéricos transitorios
        if (reason !== 'CLIENT_INITIATED' && typeof reason !== 'number') {
          setError('Se perdió la conexión con la videollamada');
        }
      });

      newRoom.on(RoomEvent.ParticipantConnected, (participant) => {
        console.log('Participant connected:', participant.identity);
        updateParticipants(newRoom);
        try {
          // Forzar suscripción a publicaciones existentes por posibles race conditions
          // Suscribirse a tracks de video
          const videoPubs = Array.from(participant.videoTrackPublications?.values?.() || []);
          videoPubs.forEach((pub) => {
            if (typeof pub.setSubscribed === 'function') {
              pub.setSubscribed(true).catch(() => {});
            }
          });
          // Suscribirse a tracks de audio (importante para el agente IA)
          const audioPubs = Array.from(participant.audioTrackPublications?.values?.() || []);
          audioPubs.forEach((pub) => {
            if (typeof pub.setSubscribed === 'function') {
              pub.setSubscribed(true).catch(() => {});
            }
          });
        } catch (_) {}
      });

      newRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
        console.log('Participant disconnected:', participant.identity);
        updateParticipants(newRoom);
      });

      newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log('Track subscribed:', track.kind, participant.identity);
        if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
          attachTrackToElement(track, participant);
        }
      });

      newRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        console.log('Track unsubscribed:', track.kind, participant.identity);
        detachTrackFromElement(track, participant);
      });

      newRoom.on(RoomEvent.TrackPublished, (publication, participant) => {
        // Asegurar suscripción inmediata al publicarse (video y audio)
        console.log('Track published:', publication.kind, participant.identity);
        try {
          if (typeof publication.setSubscribed === 'function') {
            publication.setSubscribed(true).catch((err) => {
              console.warn('Failed to subscribe to track:', err);
            });
          }
        } catch (err) {
          console.warn('Error subscribing to published track:', err);
        }
      });

      newRoom.on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => {
        console.warn('Track subscription failed for', trackSid, 'participant', participant?.identity);
      });

      // Listener para mensajes de data channel (notas y sugerencias del agente de IA)
      newRoom.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
        // Verificar que el mensaje viene del agente de IA
        if (participant && participant.identity && participant.identity.startsWith('ai_agent_')) {
          try {
            // Decodificar el payload (Uint8Array) a string
            const decoder = new TextDecoder();
            const message = decoder.decode(payload);

            // Intentar parsear como JSON
            let data;
            try {
              data = JSON.parse(message);
            } catch (e) {
              // Si no es JSON, tratarlo como texto plano
              data = { type: 'suggestion', text: message };
            }

            // Procesar según el tipo
            if (data.type === 'suggestion') {
              setSuggestions(prev => [...prev, {
                id: Date.now(),
                text: data.text || message,
                timestamp: new Date(),
                source: 'realtime', // Marcar que viene en tiempo real
              }]);
              console.log('Sugerencia recibida del agente IA:', data.text);
            } else if (data.type === 'note' || data.type === 'summary') {
              setNotes(prev => [...prev, {
                id: Date.now(),
                text: data.text || message,
                timestamp: new Date(),
                source: 'realtime', // Marcar que viene en tiempo real
              }]);
              console.log('Nota recibida del agente IA:', data.text);
            } else {
              // Si no tiene tipo, asumir que es una sugerencia
              setSuggestions(prev => [...prev, {
                id: Date.now(),
                text: data.text || message,
                timestamp: new Date(),
                source: 'realtime', // Marcar que viene en tiempo real
              }]);
            }
          } catch (e) {
            console.error('Error procesando mensaje del agente IA:', e);
            // Como fallback, tratar como sugerencia de texto plano
            try {
              const decoder = new TextDecoder();
              const text = decoder.decode(payload);
              setSuggestions(prev => [...prev, {
                id: Date.now(),
                text: text,
                timestamp: new Date(),
                source: 'realtime', // Marcar que viene en tiempo real
              }]);
            } catch (decodeError) {
              console.error('Error decodificando payload:', decodeError);
            }
          }
        }
      });

      // Conectar a la sala (usar URL provista por backend si existe)
      const urlToUse = serverUrl || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host.replace(':3000', ':7880')}`;
      await newRoom.connect(urlToUse, token, {
        autoSubscribe: true,
      });
      
      setRoom(newRoom);
      roomRef.current = newRoom;
      setAudioUnlocked(true);
      // Asegurar que la lista de participantes se actualice inmediatamente tras conectar
      updateParticipants(newRoom);

      // Publicar micrófono automáticamente para que el agente pueda escucharnos
      // (skip in observer mode — token doesn't allow publishing)
      if (!isObserverMode) {
        try {
          console.log('[VideoCall] Enabling microphone...');
          await newRoom.localParticipant.setMicrophoneEnabled(true);
          console.log('[VideoCall] Microphone enabled');
        } catch (micErr) {
          console.error('[VideoCall] Failed to enable microphone:', micErr);
        }
      }

      // Publicar cámara para roles que no sean family/observer
      if (user.role !== 'family' && !isObserverMode) {
        try {
          console.log('[VideoCall] Enabling camera...');
          await newRoom.localParticipant.setCameraEnabled(true);
          console.log('[VideoCall] Camera enabled');
        } catch (camErr) {
          console.warn('[VideoCall] Failed to enable camera:', camErr);
        }
      }

      // Desbloquear audio del navegador (necesario en algunos browsers)
      try {
        if (typeof newRoom.startAudio === 'function') {
          await newRoom.startAudio();
        }
      } catch (_) {}

      // Capturar referencias a las pistas locales publicadas
      try {
        const videoTrack = Array.from(newRoom.localParticipant.videoTrackPublications.values()).find(p => p.track)?.track || null;
        const audioTrack = Array.from(newRoom.localParticipant.audioTrackPublications.values()).find(p => p.track)?.track || null;
        setLocalTracks({ video: videoTrack, audio: audioTrack });
        if (videoTrack && localVideoRef.current) {
          videoTrack.attach(localVideoRef.current);
        }
        console.log('[VideoCall] Local tracks:', { audio: !!audioTrack, video: !!videoTrack });
      } catch (pubRefErr) {
        console.warn('Failed to capture local tracks after connect:', pubRefErr);
      }

    } catch (error) {
      // Si hubo un error pero la sala llegó a conectarse (reintentos internos), ignorarlo
      if (hasConnectedRef.current || (roomRef.current && roomRef.current.state === 2)) {
        console.warn('Transient connect error ignored after successful connection:', error);
        setIsLoading(false);
      } else {
        console.error('Error connecting to room:', error);
        setError(error.message || 'Error al conectar con la videollamada');
        setIsLoading(false);
      }
      isConnectingRef.current = false;
    }
  };

  // Actualizar lista de participantes
  const updateParticipants = (room) => {
    const remoteParticipants = Array.from(room.remoteParticipants.values());
    setParticipants(remoteParticipants);
    try {
      console.log('Remote participants count:', remoteParticipants.length, remoteParticipants.map(p => ({ id: p.identity, sid: p.sid, name: p.name })));
    } catch (_) {}
    // Adjuntar inmediatamente tracks existentes a sus elementos si ya están montados
    try {
      remoteParticipants.forEach((p) => {
        const el = remoteVideoRefs.current[p.identity];
        if (!el) return;
        const pubs = Array.from(p.videoTrackPublications?.values?.() || []);
        const existingTrack = pubs.find((pub) => pub?.track)?.track;
        if (existingTrack) {
          existingTrack.attach(el);
          el.play && el.play().catch(() => {});
        }
      });
    } catch (_) {}
  };

  // Verificar compatibilidad de getUserMedia
  const checkMediaCompatibility = () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('getUserMedia not supported');
      return false;
    }
    
    // Verificar si estamos en un contexto seguro
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      console.warn('Media access may require HTTPS');
    }
    
    return true;
  };

  // Publicar pistas locales
  const publishLocalTracks = async (room) => {
    try {
      // Verificar compatibilidad primero
      if (!checkMediaCompatibility()) {
        throw new Error('Las funciones de cámara y micrófono requieren un navegador compatible y conexión segura (HTTPS)');
      }

      console.log('Requesting camera and microphone access...');
      
      // Intentar obtener permisos primero
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        
        // Cerrar el stream de prueba
        stream.getTracks().forEach(track => track.stop());
        console.log('Media permissions granted');
      } catch (permissionError) {
        console.error('Permission error:', permissionError);
        throw new Error('Se requiere acceso a la cámara y micrófono. Por favor concede los permisos cuando te lo solicite el navegador.');
      }

      // Ahora habilitar en LiveKit
      await room.localParticipant.enableCameraAndMicrophone();
      
      // Obtener referencias a las pistas
      const videoTrack = room.localParticipant.videoTrackPublications.values().next().value?.track;
      const audioTrack = room.localParticipant.audioTrackPublications.values().next().value?.track;
      
      setLocalTracks({
        video: videoTrack,
        audio: audioTrack
      });

      // Adjuntar video local
      if (videoTrack && localVideoRef.current) {
        videoTrack.attach(localVideoRef.current);
      }

      console.log('Local tracks published successfully');

    } catch (error) {
      console.error('Error publishing local tracks:', error);
      const message = error.message || 'Error al acceder a la cámara o micrófono. Asegúrate de conceder permisos y usar HTTPS.';
      setError(message);
    }
  };

  // Habilitar audio (desbloquear autoplay) y publicar dispositivos bajo gesto del usuario
  // eslint-disable-next-line no-unused-vars
  const enableAudioAndDevices = async () => {
    const activeRoom = roomRef.current || room;
    if (!activeRoom) return;
    try {
      if (typeof activeRoom.startAudio === 'function') {
        const started = await activeRoom.startAudio();
        if (started) {
          setAudioUnlocked(true);
        }
      } else {
        setAudioUnlocked(true);
      }
      if (user.role !== 'family') {
        await publishLocalTracks(activeRoom);
      }
    } catch (e) {
      console.error('Failed to enable audio/devices:', e);
    }
  };

  // Adjuntar pista a elemento de video
  const attachTrackToElement = (track, participant) => {
    if (track.kind === Track.Kind.Video) {
      const element = remoteVideoRefs.current[participant.identity];
      if (element) {
        track.attach(element);
        try {
          element.play && element.play().catch(() => {});
        } catch (_) {}
      }
    } else if (track.kind === Track.Kind.Audio) {
      try {
        const audioEl = track.attach();
        audioEl.autoplay = true;
        audioEl.playsInline = true;
        // Para el agente IA, no silenciar el audio incluso si audioUnlocked es false
        // El usuario puede silenciar manualmente si lo desea
        audioEl.muted = false;
        remoteAudioRefs.current[participant.identity] = audioEl;
        document.body.appendChild(audioEl);
        console.log(`Audio track attached for ${participant.identity}, muted: ${audioEl.muted}, audioUnlocked: ${audioUnlocked}`);
        audioEl.play && audioEl.play().catch((err) => {
          console.warn(`Failed to play audio for ${participant.identity}:`, err);
        });
      } catch (err) {
        console.error(`Error attaching audio track for ${participant.identity}:`, err);
      }
    }
  };

  // Desadjuntar pista de elemento
  const detachTrackFromElement = (track, participant) => {
    if (track.kind === Track.Kind.Video) {
      track.detach();
    } else if (track.kind === Track.Kind.Audio) {
      try {
        const el = remoteAudioRefs.current[participant.identity];
        if (el) {
          track.detach(el);
          el.remove();
          delete remoteAudioRefs.current[participant.identity];
        } else {
          track.detach();
        }
      } catch (_) {}
    }
  };

  // Alternar micrófono
  const toggleMicrophone = async () => {
    const activeRoom = roomRef.current;
    if (!activeRoom) return;
    try {
      const newEnabled = isMuted; // si está muteado, habilitar; si no, deshabilitar
      await activeRoom.localParticipant.setMicrophoneEnabled(newEnabled);
      setIsMuted(!newEnabled);
      // Actualizar referencia al track
      const audioTrack = Array.from(activeRoom.localParticipant.audioTrackPublications.values()).find(p => p.track)?.track || null;
      setLocalTracks(prev => ({ ...prev, audio: audioTrack }));
    } catch (err) {
      console.error('Error toggling microphone:', err);
    }
  };

  // Alternar cámara
  const toggleCamera = async () => {
    const activeRoom = roomRef.current;
    if (!activeRoom) return;
    try {
      const newEnabled = !isVideoEnabled;
      await activeRoom.localParticipant.setCameraEnabled(newEnabled);
      setIsVideoEnabled(newEnabled);
      // Actualizar referencia al track
      const videoTrack = Array.from(activeRoom.localParticipant.videoTrackPublications.values()).find(p => p.track)?.track || null;
      setLocalTracks(prev => ({ ...prev, video: videoTrack }));
      if (videoTrack && localVideoRef.current) {
        videoTrack.attach(localVideoRef.current);
      }
    } catch (err) {
      console.error('Error toggling camera:', err);
    }
  };

  // Finalizar llamada
  const endCall = async () => {
    try {
      if (room) {
        await room.disconnect();
      }
      
      // Notificar al backend
      await apiRequest(`/calls/${callId}/end`, {
        method: 'POST'
      });
      
      // Redirigir al dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error ending call:', error);
      // Redirigir de todas formas
      navigate('/dashboard');
    }
  };


  // Efecto para inicializar la llamada
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      try { setLogLevel(LogLevel.debug); } catch (_) {}
    }

    const mountTime = mountTimeRef.current;

    if (callId && user) {
      fetchCallInfo();
      fetchCallNotes();
      connectToRoom();
    }

    return () => {
      const r = roomRef.current || room;
      const isDev = process.env.NODE_ENV !== 'production';
      const elapsedMs = Date.now() - mountTime;
      const shouldSkipDevCleanup = isDev && elapsedMs < 3000;
      if (!shouldSkipDevCleanup && r) {
        try { r.disconnect(); } catch (_) {}
        // Notificar al backend que la llamada terminó (keepalive para que funcione en page unload)
        try {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          const token = localStorage.getItem('token');
          fetch(`${apiUrl}/calls/${callId}/end`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
            keepalive: true,
          });
        } catch (_) {}
      }
      roomRef.current = null;
      hasConnectedRef.current = false;
      isConnectingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, user]);

  // Loading state
  if (isLoading) {
    return (
      <div className="video-call-loading">
        <div className="loading-spinner"></div>
        <p>Conectando a la videollamada...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    const isAuthError = error.includes('sesión ha expirado') || error.includes('iniciar sesión');

    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#202124',
        color: '#e8eaed'
      }}>
        <Box sx={{
          textAlign: 'center',
          maxWidth: 500,
          p: 4
        }}>
          <Avatar sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 3,
            bgcolor: isAuthError ? 'warning.light' : 'error.light'
          }}>
            <Cancel fontSize="large" />
          </Avatar>

          <Typography variant="h4" gutterBottom fontWeight="600">
            {isAuthError ? 'Sesión Expirada' : 'Error en la Videollamada'}
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            {error}
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            {isAuthError ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ borderRadius: 3 }}
                >
                  Iniciar Sesión
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.reload()}
                  sx={{ borderRadius: 3 }}
                >
                  Reintentar
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/dashboard')}
                  sx={{ borderRadius: 3 }}
                >
                  Volver al Dashboard
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.reload()}
                  sx={{ borderRadius: 3 }}
                >
                  Reintentar
                </Button>
              </>
            )}
          </Stack>

          {/* Información de debug (solo en desarrollo) */}
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, textAlign: 'left' }}>
              <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                <strong>Debug Info:</strong><br />
                Call ID: {callId}<br />
                User ID: {user?.id}<br />
                Role: {user?.role}<br />
                Error: {error}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Calcular distribución de grid tipo Meet según cantidad de tiles
  const totalTiles = participants.length + (user.role !== 'family' ? 1 : 0);
  let gridCols = '1fr';
  if (totalTiles === 2) gridCols = 'repeat(2, 1fr)';
  else if (totalTiles === 3 || totalTiles === 4) gridCols = 'repeat(2, 1fr)';
  else if (totalTiles >= 5 && totalTiles <= 9) gridCols = 'repeat(3, 1fr)';
  else if (totalTiles >= 10) gridCols = 'repeat(4, 1fr)';

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#0b0f14' }}>
      {/* AppBar — solo desktop */}
      {!isMobile && (
      <AppBar position="static" color="default" sx={{ bgcolor: '#111827', color: '#e5e7eb', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Toolbar sx={{ minHeight: 64, display: 'flex', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Videollamada</Typography>
            <Chip size="small" icon={<SignalCellularAltIcon sx={{ color: isConnected ? '#22c55e' : '#f59e0b' }} />} label={isConnected ? 'Conectado' : 'Conectando...'} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: isConnected ? '#22c55e' : '#f59e0b' }} />
            {isRecording && (
              <Chip size="small" icon={<FiberManualRecordIcon sx={{ color: '#ef4444' }} />} label="Grabando" sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: '#fecaca' }} />
            )}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip size="small" icon={<PeopleIcon sx={{ color: '#9ca3af' }} />} label={`${participants.length + (user.role !== 'family' ? 1 : 0)} participantes`} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#cbd5e1' }} />
            <Button onClick={endCall} variant="contained" color="error" startIcon={<CallEndIcon />} sx={{ textTransform: 'none', borderRadius: 2 }}>Salir</Button>
          </Stack>
        </Toolbar>
      </AppBar>
      )}

      <Container maxWidth={false} disableGutters sx={{ flex: 1, display: 'flex', gap: isMobile ? 0 : 2, position: 'relative', overflow: 'hidden', ...(isMobile ? { p: 0 } : { py: 2 }) }}>
        <Box sx={{ position: 'relative', height: '100%', flex: 1 }}>

          {/* ===== MOBILE LAYOUT: fullscreen remote + PiP local ===== */}
          {isMobile ? (
            <>
              {/* Remote participant(s) — full screen */}
              {participants.length > 0 ? (
                participants.map((participant) => {
                  const isAgent = participant.identity.startsWith('agent-') || participant.identity.startsWith('ai_agent_');

                  if (isAgent) {
                    return (
                      <Box key={participant.identity} sx={{ position: 'absolute', inset: 0 }}>
                        <AgentVisualizer room={room} />
                        <Box sx={{ position: 'absolute', top: 16, left: 16, bgcolor: 'rgba(0,0,0,0.5)', px: 1.5, py: 0.75, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SmartToyIcon sx={{ fontSize: 18, color: '#e5e7eb' }} />
                          <Typography variant="body2" sx={{ color: '#e5e7eb', fontWeight: 500 }}>Asistente Vincula</Typography>
                        </Box>
                        {isRecording && (
                          <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(239,68,68,0.7)', px: 1.25, py: 0.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#fff' }} />
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>REC</Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  return (
                    <Box key={participant.identity} sx={{ position: 'absolute', inset: 0 }}>
                      <video
                        ref={(el) => {
                          if (el) {
                            remoteVideoRefs.current[participant.identity] = el;
                            try {
                              const pubs = Array.from(participant.videoTrackPublications?.values?.() || []);
                              const existingTrack = pubs.find((p) => p?.track)?.track;
                              if (existingTrack) {
                                existingTrack.attach(el);
                                el.play && el.play().catch(() => {});
                              }
                            } catch (_) {}
                          }
                        }}
                        autoPlay
                        muted={false}
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#0b0f14' }}
                      />
                      <Box sx={{ position: 'absolute', top: 16, left: 16, bgcolor: 'rgba(0,0,0,0.5)', px: 1.5, py: 0.75, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ color: '#e5e7eb', fontWeight: 500 }}>{participant.name || participant.identity}</Typography>
                      </Box>
                      {isRecording && (
                        <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(239,68,68,0.7)', px: 1.25, py: 0.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FiberManualRecordIcon sx={{ fontSize: 10, color: '#fff' }} />
                          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>REC</Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                  <Typography>Esperando a que se unan otros participantes...</Typography>
                </Box>
              )}

              {/* Local PiP — floating corner */}
              {user.role !== 'family' && (
                <Paper
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    top: 60,
                    right: 12,
                    width: 110,
                    height: 150,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid rgba(255,255,255,0.15)',
                    zIndex: 10,
                  }}
                >
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#0b0f14', transform: 'scaleX(-1)' }}
                  />
                  <Box sx={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#e5e7eb', bgcolor: 'rgba(0,0,0,0.5)', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.65rem' }}>Tú</Typography>
                  </Box>
                </Paper>
              )}
            </>
          ) : (
          /* ===== DESKTOP LAYOUT: grid (unchanged) ===== */
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              height: '100%',
              gridTemplateColumns: gridCols,
              placeContent: 'center start',
              justifyItems: 'center',
              alignItems: 'start',
              overflow: 'auto',
            }}
          >
            {/* Remotos */}
            {participants.map((participant) => {
              const isAgent = participant.identity.startsWith('agent-') || participant.identity.startsWith('ai_agent_');

              if (isAgent) {
                return (
                  <Paper key={participant.identity} elevation={0} sx={{ bgcolor: '#111827', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', position: 'relative', width: '100%' }}>
                    <Box sx={{
                      position: 'relative',
                      width: 'min(100%, calc(80vh * 16 / 9))',
                      aspectRatio: '16 / 9',
                      maxHeight: '80vh',
                      mx: 'auto',
                    }}>
                      <AgentVisualizer room={room} />
                    </Box>
                    <Box sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,0.5)', px: 1.25, py: 0.5, borderRadius: 1.5 }}>
                      <Typography variant="body2" sx={{ color: '#e5e7eb' }}>Asistente Vincula</Typography>
                    </Box>
                  </Paper>
                );
              }

              return (
              <Paper key={participant.identity} elevation={0} sx={{ bgcolor: '#111827', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', position: 'relative', width: '100%' }}>
                <Box sx={{
                  position: 'relative',
                  width: 'min(100%, calc(80vh * 16 / 9))',
                  aspectRatio: '16 / 9',
                  maxHeight: '80vh',
                  mx: 'auto',
                }}>
                  <video
                    ref={(el) => {
                      if (el) {
                        remoteVideoRefs.current[participant.identity] = el;
                        try {
                          const pubs = Array.from(participant.videoTrackPublications?.values?.() || []);
                          const existingTrack = pubs.find((p) => p?.track)?.track;
                          if (existingTrack) {
                            existingTrack.attach(el);
                            el.play && el.play().catch(() => {});
                          }
                        } catch (_) {}
                      }
                    }}
                    autoPlay
                    muted={false}
                    playsInline
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#0b0f14' }}
                  />
                </Box>
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,0.5)', px: 1.25, py: 0.5, borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ color: '#e5e7eb' }}>{participant.name || participant.identity}</Typography>
                </Box>
              </Paper>
              );
            })}

            {/* Local */}
            {user.role !== 'family' && (
              <Paper elevation={0} sx={{ bgcolor: '#111827', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', position: 'relative', width: '100%' }}>
                <Box sx={{
                  position: 'relative',
                  width: 'min(100%, calc(80vh * 16 / 9))',
                  aspectRatio: '16 / 9',
                  maxHeight: '80vh',
                  mx: 'auto',
                }}>
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#0b0f14' }}
                  />
                </Box>
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,0.5)', px: 1.25, py: 0.5, borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ color: '#e5e7eb' }}>Tú</Typography>
                </Box>
              </Paper>
            )}

            {participants.length === 0 && (
              <Box sx={{ gridColumn: '1 / -1', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                <Typography>Esperando a que se unan otros participantes...</Typography>
              </Box>
            )}
          </Box>
          )}
        </Box>

        {/* Sidebar de Notas y Sugerencias (solo para empleados) */}
        {user.role === 'employee' && (
          <Drawer
            variant="persistent"
            anchor="right"
            open={sidebarOpen}
            sx={{
              width: sidebarOpen ? 400 : 0,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 400,
                boxSizing: 'border-box',
                position: 'relative',
                height: '100%',
                borderLeft: '1px solid rgba(255,255,255,0.08)',
                bgcolor: '#111827',
                color: '#e5e7eb',
              },
            }}
          >
            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <SmartToyIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Asistente IA
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setSidebarOpen(false)}
                  sx={{ ml: 'auto', color: '#9ca3af' }}
                >
                  <Typography variant="body2">×</Typography>
                </IconButton>
              </Stack>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />

              {/* Notas de la conversación */}
              <Box sx={{ mb: 3, flex: '0 0 auto' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <NotesIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#cbd5e1' }}>
                    Notas de la Conversación
                  </Typography>
                </Stack>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {notes.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic', p: 1 }}>
                      No hay notas aún...
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {notes.map((note) => (
                        <Card key={note.id} sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="body2" sx={{ color: '#e5e7eb', fontSize: '0.875rem' }}>
                              {note.text}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
                              {new Date(note.timestamp).toLocaleTimeString()}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2 }} />

              {/* Sugerencias */}
              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <LightbulbIcon sx={{ fontSize: 20, color: '#fbbf24' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#cbd5e1' }}>
                    Sugerencias
                  </Typography>
                </Stack>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {suggestions.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic', p: 1 }}>
                      El asistente IA generará sugerencias durante la conversación...
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {suggestions.map((suggestion) => (
                        <Card key={suggestion.id} sx={{ bgcolor: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)' }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="body2" sx={{ color: '#fef3c7', fontSize: '0.875rem', lineHeight: 1.6 }}>
                              {suggestion.text}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block', mt: 0.5 }}>
                              {new Date(suggestion.timestamp).toLocaleTimeString()}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            </Box>
          </Drawer>
        )}

        {/* Botón para abrir sidebar si está cerrado */}
        {user.role === 'employee' && !sidebarOpen && (
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              position: 'fixed',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: '#111827',
              color: '#e5e7eb',
              border: '1px solid rgba(255,255,255,0.08)',
              '&:hover': { bgcolor: '#1f2937' },
            }}
          >
            <SmartToyIcon />
          </IconButton>
        )}
      </Container>

      {/* Barra de controles inferior */}
      <Box sx={{
        position: isMobile ? 'absolute' : 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: isMobile ? 'transparent' : 'rgba(17,24,39,0.85)',
        backdropFilter: isMobile ? 'none' : 'blur(6px)',
        borderTop: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
        zIndex: 20,
        pb: isMobile ? 'env(safe-area-inset-bottom, 12px)' : 0,
      }}>
        <Container maxWidth={false}>
          <Stack direction="row" spacing={isMobile ? 4 : 2} alignItems="center" justifyContent="center" sx={{ py: isMobile ? 2 : 1.5 }}>
            <Tooltip title={isMuted ? 'Activar micrófono' : 'Silenciar micrófono'}>
              <IconButton onClick={toggleMicrophone} sx={{
                bgcolor: isMuted ? 'rgba(127,29,29,0.9)' : 'rgba(31,41,55,0.8)',
                color: isMuted ? '#fecaca' : '#e5e7eb',
                '&:hover': { bgcolor: isMuted ? '#991b1b' : '#374151' },
                width: isMobile ? 56 : 40,
                height: isMobile ? 56 : 40,
                backdropFilter: 'blur(8px)',
              }}>
                {isMuted ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isVideoEnabled ? 'Desactivar cámara' : 'Activar cámara'}>
              <IconButton onClick={toggleCamera} sx={{
                bgcolor: !isVideoEnabled ? 'rgba(127,29,29,0.9)' : 'rgba(31,41,55,0.8)',
                color: !isVideoEnabled ? '#fecaca' : '#e5e7eb',
                '&:hover': { bgcolor: !isVideoEnabled ? '#991b1b' : '#374151' },
                width: isMobile ? 56 : 40,
                height: isMobile ? 56 : 40,
                backdropFilter: 'blur(8px)',
              }}>
                {isVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Finalizar llamada">
              <IconButton onClick={endCall} sx={{
                bgcolor: '#dc2626',
                color: '#fff',
                '&:hover': { bgcolor: '#b91c1c' },
                width: isMobile ? 64 : 40,
                height: isMobile ? 64 : 40,
              }}>
                <CallEndIcon sx={{ fontSize: isMobile ? 28 : 24 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default VideoCall;